/**
 * The craft reveal in the Forge's centre "?" node (crafting.md §7.1), driven by revealModel's
 * RevealSequencer: anticipation glow → colour climb (only up to the true rarity) → hit + burst →
 * the crafted item settles into the node. Click or Space skips. Reduced motion halves timings
 * and drops the shake (revealModel). The honest roll numbers live in the Forge (i) sheet.
 *
 * Cues are dispatched as `keystone:sfx` window events for whatever audio layer listens.
 */
import { el } from '../ui/dom';
import { RARITY_COLOR, RARITY_LABEL } from '../ui/rarity';
import { type ClimbColor, type RevealPlan, RevealSequencer } from './revealModel';
import './altarForge.css';

export const SET_COLOR = '#3fe0d0';

export const climbColor = (c: ClimbColor): string => (c === 'set' ? SET_COLOR : RARITY_COLOR[c]);

/** Fire a juice / sound cue for whatever audio layer listens. */
export function emitCue(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('keystone:sfx', { detail: { id } }));
  } catch {
    /* no CustomEvent: ignore */
  }
}

/** Burst of DOM particles in `host` (CSS animates them; removed after `ms`). */
export function particleBurst(host: HTMLElement, n: number, colors: readonly string[], opts: { spread?: number; ms?: number } = {}): void {
  if (n <= 0) return;
  const spread = opts.spread ?? 160;
  const ms = opts.ms ?? 1100;
  const box = el('div', 'kfx-burst', host);
  for (let i = 0; i < n; i++) {
    const s = el('span', 'kfx-p', box);
    const a = (i / n) * Math.PI * 2 + Math.random() * 0.6;
    const d = spread * (0.45 + Math.random() * 0.55);
    s.style.setProperty('--dx', `${Math.cos(a) * d}px`);
    s.style.setProperty('--dy', `${Math.sin(a) * d - 20}px`);
    s.style.setProperty('--d', `${Math.round(Math.random() * 120)}ms`);
    s.style.setProperty('--s', `${(3 + Math.random() * 4).toFixed(1)}px`);
    s.style.setProperty('--pc', colors[i % colors.length]!);
  }
  setTimeout(() => box.remove(), ms + 200);
}

/** A ring that expands and fades (shockwave). */
export function shockwave(host: HTMLElement, color: string, big = false): void {
  const r = el('div', `kfx-wave${big ? ' big' : ''}`, host);
  r.style.setProperty('--wc', color);
  setTimeout(() => r.remove(), 900);
}

export interface NodeRevealOptions {
  plan: RevealPlan;
  /** Fill the node with the result (called once, when the reveal lands or is skipped). */
  settle: (host: HTMLElement) => void;
  /** Called once the result is shown. */
  onDone?: () => void;
}

export interface RevealHandle {
  skip(): void;
  dispose(): void;
  readonly done: boolean;
}

/** Play a craft reveal inside `host` (the centre node box: it is cleared and refilled). */
export function playNodeReveal(host: HTMLElement, o: NodeRevealOptions): RevealHandle {
  const plan = o.plan;
  const seq = new RevealSequencer(plan);
  host.replaceChildren();
  host.classList.remove('settled', 'hit');
  host.classList.add('revealing');
  if (plan.reducedMotion) host.classList.add('rm');
  const glow = el('div', 'kaf-core-glow', host);
  const box = el('div', 'kaf-core-box', host);
  el('span', 'kaf-core-q', box, '?');
  const label = el('div', 'kaf-core-flash', host, '');
  void glow;

  let raf = 0;
  let finished = false;
  let disposed = false;
  let lastStep = -2;
  let settled = false;

  const settle = (): void => {
    if (settled) return;
    settled = true;
    box.remove();
    label.remove();
    o.settle(host);
  };

  const finish = (): void => {
    if (finished) return;
    finished = true;
    settle();
    host.classList.remove('revealing');
    host.classList.add('settled');
    o.onDone?.();
  };

  const onCue = (id: string): void => {
    emitCue(id);
    if (id.startsWith('sfx.reveal.')) {
      const color = climbColor(plan.climb[plan.climb.length - 1] ?? 'common');
      settle();
      if (plan.particles) particleBurst(host, Math.min(plan.particles, 28), plan.tierPiece ? [color, SET_COLOR, '#f6e2a8'] : [color, '#ffffff'], { spread: 140 });
      if (plan.rarity !== 'common') shockwave(host, color, plan.rarity === 'epic' || plan.rarity === 'legendary' || plan.tierPiece);
      host.classList.add('hit');
    }
  };

  const frame = (now: number): void => {
    if (disposed) return;
    const { frame: f, cues } = seq.tick(now);
    for (const c of cues) onCue(c.id);
    host.style.setProperty('--c', climbColor(f.color));
    host.dataset['phase'] = f.phase;
    if (f.step !== lastStep) {
      lastStep = f.step;
      host.classList.remove('pulse');
      void host.offsetWidth;
      host.classList.add('pulse');
      label.textContent = f.phase === 'anticipation' ? '' : f.color === 'set' ? 'Tier piece' : RARITY_LABEL[f.color];
    }
    if (f.shake > 0) {
      const a = Math.min(4, f.shake);
      host.style.transform = `translate(${((Math.random() - 0.5) * a).toFixed(1)}px, ${((Math.random() - 0.5) * a).toFixed(1)}px)`;
    } else host.style.transform = '';
    if (f.phase === 'done') {
      host.style.transform = '';
      finish();
      return;
    }
    raf = requestAnimationFrame(frame);
  };

  const skip = (): void => {
    if (seq.skip()) {
      cancelAnimationFrame(raf);
      frame(performance.now());
    }
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.code !== 'Space' || finished) return;
    e.preventDefault();
    e.stopPropagation();
    skip();
  };
  const onDown = (e: PointerEvent): void => {
    if (finished || e.button !== 0) return;
    skip();
  };
  window.addEventListener('keydown', onKey, true);
  host.addEventListener('pointerdown', onDown);
  raf = requestAnimationFrame(frame);

  return {
    skip,
    dispose() {
      disposed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey, true);
      host.removeEventListener('pointerdown', onDown);
      host.style.transform = '';
    },
    get done() {
      return finished;
    },
  };
}
